import { useCallback } from 'react';
import { registrarActividad, registrarLogin, registrarLogout } from '../servicios/auditoria/servicios';
import { useAuth } from '../componentes/Context';

interface DetallesAuditoria {
  [key: string]: any;
}

/**
 * Hook personalizado para manejar la auditoría de acciones en el sistema
 */
export const useAuditoria = () => {
  const auth = useAuth();
  const token = auth.token;
  const sucursal = auth.sucursal;
  const currentUser = auth.currentUser;
  
  /**
   * Registra una acción del usuario en el sistema
   * Solo audita operaciones de creación, actualización, eliminación y otras acciones específicas
   * NO audita operaciones de consulta para reducir la carga y el almacenamiento
   * 
   * @param accion Tipo de acción (crear, editar, eliminar, etc.)
   * @param seccion Sección del sistema donde se realiza la acción
   * @param detalles Detalles adicionales de la acción
   * @returns Promise<boolean> True si se registró correctamente
   */
  const auditar = useCallback(async (
    accion: string, 
    seccion: string, 
    detalles: DetallesAuditoria = {}
  ): Promise<boolean> => {
    // Lista de acciones que NO se auditan (consultas)
    const accionesNoAuditables = ['consultar', 'consultar_detalle', 'consultar_coleccion'];
    
    // Verificar si la acción debe omitirse (consultas)
    if (accionesNoAuditables.includes(accion.toLowerCase())) {
      return false;
    }
    
    if (!currentUser?.uid || !token || !sucursal) {
      return false;
    }
    
    try {
      const respuesta = await registrarActividad(
        token,
        currentUser.uid,
        accion,
        seccion,
        sucursal,
        detalles
      );
      
      return respuesta.success;
    } catch (error) {
      console.error('Error al registrar actividad en auditoría:', error);
      return false;
    }
  }, [currentUser, token, sucursal]);
  
  /**
   * Registra un inicio de sesión en el sistema
   * 
   * @param rol Rol del usuario (por defecto 'admin')
   * @returns Promise<boolean> True si se registró correctamente
   */
  const auditarLogin = useCallback(async (rol: string = 'admin'): Promise<boolean> => {
    if (!currentUser?.uid || !token || !sucursal) {
      return false;
    }
    
    try {
      const respuesta = await registrarLogin(
        token,
        currentUser.uid,
        sucursal,
        rol
      );
      
      return respuesta.success;
    } catch (error) {
      console.error('Error al registrar login en auditoría:', error);
      return false;
    }
  }, [currentUser, token, sucursal]);
  
  /**
   * Registra un cierre de sesión en el sistema
   * 
   * @param rol Rol del usuario (por defecto 'admin')
   * @returns Promise<boolean> True si se registró correctamente
   */
  const auditarLogout = useCallback(async (rol: string = 'admin'): Promise<boolean> => {
    if (!currentUser?.uid || !token || !sucursal) {
      return false;
    }
    
    try {
      const respuesta = await registrarLogout(
        token,
        currentUser.uid,
        sucursal,
        rol
      );
      
      return respuesta.success;
    } catch (error) {
      console.error('Error al registrar logout en auditoría:', error);
      return false;
    }
  }, [currentUser, token, sucursal]);
  
  return {
    auditar,
    auditarLogin,
    auditarLogout
  };
};
